import React from 'react';
import Icon from '../../../components/AppIcon';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const MLModelMonitor = ({ model }) => {
  const getPerformanceColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 70) return 'text-warning';
    return 'text-error';
  };

  const getPerformanceBg = (score) => {
    if (score >= 90) return 'bg-success/10';
    if (score >= 70) return 'bg-warning/10';
    return 'bg-error/10';
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-2 shadow-elevation-3">
          <p className="text-xs font-medium text-foreground mb-1">{payload?.[0]?.payload?.time}</p>
          <p className="text-xs text-primary">Accuracy: {payload?.[0]?.value}%</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 shadow-elevation-2 border border-border">
      <div className="flex items-start justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Brain" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-sm md:text-base font-semibold text-foreground mb-1">{model?.name}</h3>
            <p className="text-xs md:text-sm text-muted-foreground">{model?.type}</p>
          </div>
        </div>
        <div className={`px-3 py-1.5 rounded-full ${getPerformanceBg(model?.accuracy)} ${getPerformanceColor(model?.accuracy)} text-xs md:text-sm font-medium`}>
          {model?.status}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
        <div className="p-3 bg-muted/50 rounded-lg">
          <p className="text-xs text-muted-foreground mb-1">Accuracy</p>
          <p className={`text-lg md:text-xl font-semibold ${getPerformanceColor(model?.accuracy)}`}>
            {model?.accuracy}%
          </p>
        </div>
        <div className="p-3 bg-muted/50 rounded-lg">
          <p className="text-xs text-muted-foreground mb-1">Precision</p>
          <p className={`text-lg md:text-xl font-semibold ${getPerformanceColor(model?.precision)}`}>
            {model?.precision}%
          </p>
        </div>
        <div className="p-3 bg-muted/50 rounded-lg">
          <p className="text-xs text-muted-foreground mb-1">Recall</p>
          <p className={`text-lg md:text-xl font-semibold ${getPerformanceColor(model?.recall)}`}>
            {model?.recall}%
          </p>
        </div>
        <div className="p-3 bg-muted/50 rounded-lg">
          <p className="text-xs text-muted-foreground mb-1">F1 Score</p>
          <p className={`text-lg md:text-xl font-semibold ${getPerformanceColor(model?.f1Score)}`}>
            {model?.f1Score}%
          </p>
        </div>
      </div>
      <div className="mb-4">
        <h4 className="text-sm font-medium text-foreground mb-3">Performance Trend (Last 7 Days)</h4>
        <div className="w-full h-40 md:h-48" aria-label="ML Model Performance Trend Chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={model?.performanceData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="time"
                tick={{ fill: 'var(--color-muted-foreground)', fontSize: 10 }}
              />
              <YAxis
                tick={{ fill: 'var(--color-muted-foreground)', fontSize: 10 }}
                domain={[0, 100]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="accuracy"
                stroke="var(--color-primary)"
                fill="var(--color-primary)"
                fillOpacity={0.2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs md:text-sm">
        <div className="flex items-center gap-2">
          <Icon name="Calendar" size={14} className="text-muted-foreground" />
          <span className="text-muted-foreground">Last Trained:</span>
          <span className="font-medium text-foreground">{model?.lastTrained}</span>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="Database" size={14} className="text-muted-foreground" />
          <span className="text-muted-foreground">Training Data:</span>
          <span className="font-medium text-foreground">{model?.trainingDataSize}</span>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="Zap" size={14} className="text-muted-foreground" />
          <span className="text-muted-foreground">Predictions/Day:</span>
          <span className="font-medium text-foreground">{model?.predictionsPerDay?.toLocaleString()}</span>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="TrendingUp" size={14} className="text-muted-foreground" />
          <span className="text-muted-foreground">Version:</span>
          <span className="font-medium text-foreground">{model?.version}</span>
        </div>
      </div>
    </div>
  );
};

export default MLModelMonitor;